import React, { useState } from 'react';
import { TLanguageIDs, TFileId, ICustomGroups, TMediaFileType } from 'types';
import { Radio, RadioChangeEvent } from 'antd';
import StyleSettingsImageUploader from 'components/StyleSettingsImageUploader/StyleSettingsImageUploader';
import Text from 'components/Text/Text';
import { FileSectionForStyleSettings } from 'components/UploaderAndDownloader/FileSectionForStyleSettings';
import { aspectRatiosForStyleSettings } from 'appConstants';
import style from './NormalDisplay.module.scss';

interface INormalDisplay {
	group: ICustomGroups;
	languageId: TLanguageIDs;
	groupLoading: boolean;
	isMakeImageRequest: boolean;
	isDisabledControls?: boolean;
	showGroupLoader: () => void;
	hideGroupLoader: () => void;
	onPDFMenuSizeError(): void;
	setMediaSettingsFromNormalDisplay: (
		languageId: null | TLanguageIDs,
		groupId: string,
		translationId: string,
		mediaFileType: TMediaFileType,
		sourceUrl: string,
		fileId: TFileId,
	) => void;
}

const NormalDisplay: React.FC<INormalDisplay> = ({
	group,
	languageId,
	isDisabledControls,
	showGroupLoader,
	hideGroupLoader,
	onPDFMenuSizeError,
	groupLoading,
	isMakeImageRequest,
	setMediaSettingsFromNormalDisplay,
}) => {
	const [imageOrientation, setImageOrientation] = useState<'horizontal' | 'vertical'>('horizontal');
	const isSlider = group.isMultipleItems;
	const isMultipleImagesInBlock = group.key === 'cards' || group.key === 'split_payment_images';
	const listOfTypesOfMediaContent = [1, 2];

	const PDFFilesOfBlockWithMultipleImages = isMultipleImagesInBlock
		? group.items.filter((item) => item.mediaFileType === 3)
		: [];

	const changeOrientation = (e: RadioChangeEvent) => {
		setImageOrientation(e.target.value);
	};

	const handleUploadPdf = (fileId: string, translationId: string, sourceUrl: string) => {
		setMediaSettingsFromNormalDisplay(languageId, group.id, translationId, 3, sourceUrl, fileId);
	};

	const handleDeletePdf = (translationId: string) => {
		setMediaSettingsFromNormalDisplay(languageId, group.id, translationId, 3, '', null);
	};

	return (
		<div className={style.container}>
			<Radio.Group
				className={style.orientationSwitch}
				value={imageOrientation}
				disabled={isDisabledControls}
				onChange={changeOrientation}>
				<Radio className={style.orientationButton} value="horizontal">
					<Text
						className={style.orientationText}
						translationNameSpace="listing"
						translationKey="horizontalOrientationLabel"
						size="s"
					/>
				</Radio>
				<Radio className={style.orientationButton} value="vertical">
					<Text
						className={style.orientationText}
						translationNameSpace="listing"
						translationKey="verticalOrientationLabel"
						size="s"
					/>
				</Radio>
			</Radio.Group>
			<div className={style.images}>
				{group.items.map((el) =>
					listOfTypesOfMediaContent.includes(el.mediaFileType) ? (
						<StyleSettingsImageUploader
							key={el.translationId}
							filePath={el.filePath}
							translationId={el.translationId}
							isNeedImgHeading={!isSlider && isMultipleImagesInBlock}
							itemKey={el.key}
							mediaFileType={el.mediaFileType}
							maxFileSizeMB={{ img: 1, gif: isMultipleImagesInBlock ? 15 : 1 }}
							imageOrientation={imageOrientation}
							languageId={languageId}
							groupId={group.id}
							groupKey={group.key}
							verticalAspectRatio={aspectRatiosForStyleSettings[group.key]?.vertical}
							horizontalAspectRatio={aspectRatiosForStyleSettings[group.key]?.horizontal}
							isDisabledControls={isDisabledControls}
							showGroupLoader={showGroupLoader}
							hideGroupLoader={hideGroupLoader}
							setMediaSettingsFromNormalDisplay={setMediaSettingsFromNormalDisplay}
							groupLoading={groupLoading}
							isMakeImageRequest={isMakeImageRequest}
						/>
					) : (
						<React.Fragment key={el.translationId}></React.Fragment>
					),
				)}
			</div>

			<div className={style.additionalFiles}>
				{!!PDFFilesOfBlockWithMultipleImages.length &&
					PDFFilesOfBlockWithMultipleImages.map((item) => (
						<div key={item.translationId} className={style.additionalFile}>
							<Text
								translationNameSpace="mediaSettings"
								translationKey={item.key}
								theme="secondary"
								size="l"
								weight="bold"
								display="inline"
							/>
							<FileSectionForStyleSettings
								onUploadFile={(fileId, sourceUrl) =>
									handleUploadPdf(fileId, item.translationId, sourceUrl)
								}
								onDeleteFile={() => handleDeletePdf(item.translationId)}
								onSizeError={onPDFMenuSizeError}
								item={item}
								maxFileSizeMB={20}
								isDisabledControls={item.key === 'image_menu_activation' ? false : isDisabledControls}
								accept={['pdf']}
							/>
						</div>
					))}
			</div>
		</div>
	);
};

export default NormalDisplay;
